import { Link } from 'react-router-dom';
import type { SwotCategory, SwotEntry } from './types';
import {
  SWOT_CATEGORIES,
  SWOT_CATEGORY_LABEL,
  SWOT_QUADRANT_LABEL,
  groupSwotByCategory,
} from './swotUtils';

const QUADRANT_CLASS: Record<SwotCategory, string> = {
  fortaleza: 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800/60',
  debilidad: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800/60',
  oportunidad: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800/60',
  amenaza: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/60',
};

interface SwotMatrixProps {
  entries: SwotEntry[];
  canEdit: boolean;
  canPromote?: boolean;
  promotingId?: string | null;
  onPromote?: (entry: SwotEntry) => void;
  onEdit: (entry: SwotEntry) => void;
  onDeactivate: (entry: SwotEntry) => void;
}

function QuadrantCell({
  category,
  entries,
  canEdit,
  canPromote,
  promotingId,
  onEdit,
  onDeactivate,
  onPromote,
}: {
  category: SwotCategory;
  entries: SwotEntry[];
  canEdit: boolean;
  canPromote: boolean;
  promotingId: string | null;
  onEdit: (entry: SwotEntry) => void;
  onDeactivate: (entry: SwotEntry) => void;
  onPromote: (entry: SwotEntry) => void;
}) {
  return (
    <div className={`rounded-lg border p-4 min-h-[180px] ${QUADRANT_CLASS[category]}`}>
      <div className='mb-3'>
        <h3 className='font-semibold text-sm'>{SWOT_CATEGORY_LABEL[category]}</h3>
        <p className='text-xs text-muted-foreground'>{SWOT_QUADRANT_LABEL[category]}</p>
      </div>
      <ul className='space-y-2' data-testid={`swot-quadrant-${category}`}>
        {entries.length === 0 && (
          <li className='text-xs text-muted-foreground italic'>Sin entradas activas</li>
        )}
        {entries.map((entry) => (
          <li
            key={entry.id}
            className='rounded-md bg-background/80 border border-border/60 p-2 text-sm dark:bg-card/90 dark:text-card-foreground'
          >
            <p>{entry.description}</p>
            {entry.impact && (
              <p className='text-xs text-muted-foreground mt-1'>Impacto: {entry.impact}</p>
            )}
            {entry.priorityIndex != null && (
              <p className='text-xs text-muted-foreground mt-1'>
                Índice: <strong>{entry.priorityIndex}</strong>
              </p>
            )}
            {entry.promotedRiskId && (
              <Link
                to={`/riesgos/${entry.promotedRiskId}`}
                className='text-xs text-primary hover:underline mt-1 inline-block'
              >
                Promovido a matriz
              </Link>
            )}
            {canEdit && (
              <div className='mt-2 flex flex-wrap gap-2'>
                <button
                  type='button'
                  onClick={() => onEdit(entry)}
                  className='text-xs text-primary hover:underline'
                >
                  Editar
                </button>
                <button
                  type='button'
                  onClick={() => onDeactivate(entry)}
                  className='text-xs text-destructive hover:underline'
                >
                  Dar de baja
                </button>
                {canPromote && !entry.promotedRiskId && (
                  <button
                    type='button'
                    disabled={promotingId === entry.id}
                    onClick={() => onPromote(entry)}
                    className='text-xs text-primary hover:underline disabled:opacity-50'
                  >
                    {promotingId === entry.id ? 'Promoviendo…' : 'Promover'}
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SwotMatrix({
  entries,
  canEdit,
  canPromote = false,
  promotingId = null,
  onPromote,
  onEdit,
  onDeactivate,
}: SwotMatrixProps) {
  const grouped = groupSwotByCategory(entries);

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4' data-testid='swot-matrix'>
      {SWOT_CATEGORIES.map((category) => (
        <QuadrantCell
          key={category}
          category={category}
          entries={grouped[category]}
          canEdit={canEdit}
          canPromote={canPromote}
          promotingId={promotingId}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onPromote={onPromote ?? (() => undefined)}
        />
      ))}
    </div>
  );
}
